import Component from "@base/Component.js";
import CockpitPanel from "./CockpitPanel.js";
import api from "@api/api.js";
import t from "@/services/translationService.js";

/**
 * Cockpit → My Account. Enrol, inspect and disable the SIGNED-IN account's
 * second factor (TOTP).
 *
 * Named for the account rather than "Security" on purpose: every other Cockpit
 * section administers the site, and sitting among them this read as a site-wide
 * setting. It is not — the backend resolves the account from req.user and never
 * from an id in the request, so nobody can enrol or disable a factor on someone
 * else's behalf. Coverage across all accounts is visible in User Manager.
 *
 * props: { lang }
 *
 * Two deliberate differences from the list sections (#135), the same ones Hero
 * settles in #132: there is no empty state, because a settings screen has no
 * list to be empty; and disabling two-factor auth confirms in place rather than
 * from inside an editor, because there is no row to open — the confirm dialog
 * is the second step that opening a row provides elsewhere. Disabling also
 * emits a fatal security event, so the dialog is not the only guard.
 *
 * `auth.*` keys are shared with the login modal on purpose: the password and
 * code fields are the same fields, and duplicating their labels would let the
 * two screens drift apart.
 */
export default class SecurityPanel extends CockpitPanel {
  constructor(props) {
    super(props);
    // view: "idle" | "enrolling" | "codes"
    this.state = { view: "idle", status: null, setup: null, recoveryCodes: null, error: "", busy: false };
  }

  async onBeforeMount() {
    Object.assign(this.state, await this.fetchStatus());
  }

  async fetchStatus() {
    try {
      return { status: await api.get("/api/auth/totp"), error: "" };
    } catch (err) {
      return { status: null, error: err.message || t.t("common.error") };
    }
  }

  title() {
    return Component.escape(t.t("cockpit.nav.account"));
  }

  lead() {
    return Component.escape(t.t("cockpit.account.lead"));
  }

  #statusBadge() {
    const enabled = this.state.status?.enabled;
    return `<span class="state-badge state-badge--${enabled ? "public" : "draft"}">${Component.escape(
      enabled ? t.t("cockpit.account.enabled") : t.t("cockpit.account.disabled")
    )}</span>`;
  }

  /** The one-time list of recovery codes, shown immediately after enrolling. */
  #renderCodes() {
    const codes = this.state.recoveryCodes || [];
    return `
      <div class="cockpit-2fa">
        <p class="cockpit-2fa__warning">${Component.escape(t.t("cockpit.account.codesWarning"))}</p>
        <ul class="cockpit-2fa__codes">
          ${codes.map((code) => `<li><code>${Component.escape(code)}</code></li>`).join("")}
        </ul>
        <div class="cockpit-2fa__actions">
          <button type="button" class="btn-secondary" data-action="copy-codes">${Component.escape(t.t("cockpit.account.copyCodes"))}</button>
          <button type="button" class="btn-primary" data-action="done-codes">${Component.escape(t.t("cockpit.account.codesSaved"))}</button>
        </div>
      </div>`;
  }

  /** QR + manual secret + the confirming code field. */
  #renderEnrolling() {
    const { setup } = this.state;
    return `
      <div class="cockpit-2fa">
        <ol class="cockpit-2fa__steps">
          <li>${Component.escape(t.t("cockpit.account.step1"))}</li>
          <li>${Component.escape(t.t("cockpit.account.step2"))}</li>
        </ol>
        <div class="cockpit-2fa__enrol">
          <img class="cockpit-2fa__qr" src="${Component.escape(setup.qrDataUrl)}" alt="${Component.escape(t.t("cockpit.account.qrAlt"))}" />
          <div>
            <p class="cockpit-2fa__manual-label">${Component.escape(t.t("cockpit.account.manualEntry"))}</p>
            <code class="cockpit-2fa__secret">${Component.escape(setup.secret)}</code>
            <form class="cockpit-2fa__form">
              <label>${Component.escape(t.t("auth.totpCode"))}
                <input type="text" name="code" inputmode="numeric" pattern="[0-9]*" maxlength="6"
                       autocomplete="one-time-code" spellcheck="false" required />
              </label>
              <div class="form-error" role="alert">${Component.escape(this.state.error)}</div>
              <button type="submit" class="btn-primary" ${this.state.busy ? "disabled" : ""}>
                ${Component.escape(t.t("cockpit.account.confirmEnable"))}
              </button>
              <button type="button" class="btn-secondary" data-action="cancel-enrol">${Component.escape(t.t("common.cancel"))}</button>
            </form>
          </div>
        </div>
      </div>`;
  }

  #renderIdle() {
    const status = this.state.status;
    if (!status) return CockpitPanel.error(this.state.error || t.t("common.error"));

    if (!status.configured) {
      return CockpitPanel.error(t.t("cockpit.account.notConfigured"));
    }

    if (!status.enabled) {
      return `
        <div class="cockpit-2fa">
          <p>${Component.escape(t.t("cockpit.account.offExplainer"))}</p>
          ${this.state.error ? CockpitPanel.error(this.state.error) : ""}
          <button type="button" class="btn-primary" data-action="start-enrol" ${this.state.busy ? "disabled" : ""}>
            ${Component.escape(t.t("cockpit.account.enable"))}
          </button>
        </div>`;
    }

    const remaining = status.recoveryCodesRemaining ?? 0;
    return `
      <div class="cockpit-2fa">
        <p>${Component.escape(t.t("cockpit.account.onExplainer"))}</p>
        <p class="${remaining <= 2 ? "cockpit-2fa__warning" : "cockpit-hint"}">
          ${Component.escape(t.t("cockpit.account.codesRemaining").replace("{count}", String(remaining)))}
        </p>
        ${this.state.error ? CockpitPanel.error(this.state.error) : ""}
        <form class="cockpit-2fa__form cockpit-2fa__form--disable">
          <p class="cockpit-hint">${Component.escape(t.t("cockpit.account.disableHint"))}</p>
          <label>${Component.escape(t.t("auth.password"))}
            <input type="password" name="password" autocomplete="current-password" required />
          </label>
          <label>${Component.escape(t.t("auth.totpCode"))}
            <input type="text" name="code" inputmode="numeric" pattern="[0-9]*" maxlength="6"
                   autocomplete="one-time-code" spellcheck="false" required />
          </label>
          <button type="submit" class="btn-danger" ${this.state.busy ? "disabled" : ""}>
            ${Component.escape(t.t("cockpit.account.disable"))}
          </button>
        </form>
      </div>`;
  }

  actions() {
    return this.state.view === "idle" && this.state.status ? this.#statusBadge() : "";
  }

  body() {
    if (this.state.view === "codes") return this.#renderCodes();
    if (this.state.view === "enrolling") return this.#renderEnrolling();
    return this.#renderIdle();
  }

  onMounted() {
    const start = this.$('[data-action="start-enrol"]');
    if (start) this.listen(start, "click", () => this.#startEnrolment());

    const cancel = this.$('[data-action="cancel-enrol"]');
    if (cancel) this.listen(cancel, "click", () => this.setState({ view: "idle", setup: null, error: "" }));

    const done = this.$('[data-action="done-codes"]');
    if (done) this.listen(done, "click", () => this.#finishCodes());

    const copy = this.$('[data-action="copy-codes"]');
    if (copy) {
      this.listen(copy, "click", async () => {
        await navigator.clipboard?.writeText((this.state.recoveryCodes || []).join("\n"));
        copy.textContent = t.t("cockpit.account.copied");
      });
    }

    const enrolForm = this.$(".cockpit-2fa__form:not(.cockpit-2fa__form--disable)");
    if (enrolForm) this.listen(enrolForm, "submit", (e) => this.#confirmEnrolment(e));

    const disableForm = this.$(".cockpit-2fa__form--disable");
    if (disableForm) this.listen(disableForm, "submit", (e) => this.#disable(e));
  }

  async #startEnrolment() {
    this.setState({ busy: true, error: "" });
    try {
      const setup = await api.post("/api/auth/totp/setup", {});
      this.setState({ view: "enrolling", setup, busy: false });
    } catch (err) {
      this.setState({ busy: false, error: err.message || t.t("common.error") });
    }
  }

  async #confirmEnrolment(event) {
    event.preventDefault();
    const code = this.$('input[name="code"]').value.trim();
    this.setState({ busy: true, error: "" });
    try {
      const res = await api.post("/api/auth/totp/enable", { code });
      // Enabling revokes every existing session server-side, this one included —
      // so show the codes first and only then force the re-login.
      this.setState({ view: "codes", recoveryCodes: res.recoveryCodes, busy: false });
    } catch (err) {
      this.setState({ busy: false, error: err.message || t.t("cockpit.account.badCode") });
    }
  }

  /** Existing sessions were revoked when 2FA was enabled, so reload to sign in again. */
  #finishCodes() {
    window.location.reload();
  }

  async #disable(event) {
    event.preventDefault();
    if (!window.confirm(t.t("cockpit.account.confirmDisable"))) return;

    const password = this.$('input[name="password"]').value;
    const code = this.$('input[name="code"]').value.trim();
    this.setState({ busy: true, error: "" });
    try {
      await api.post("/api/auth/totp/disable", { password, code });
      window.location.reload();
    } catch (err) {
      this.setState({ busy: false, error: err.message || t.t("common.error") });
    }
  }
}
